import { createContext, useCallback, useEffect, useRef, useState } from "react";

import { useField, useFormikContext } from "formik";
import { get } from "lodash";
import { useDispatch, useSelector } from "react-redux";

import { lookupBusinessRegistration } from "actions/businessRegistrations";

import { withNamespace } from "components/Form/FormikControls";

import { validateABN, validateACN } from "lib/validation/validators";

import { getBusinessRegistrationById } from "selectors/businessRegistrations";

const LOOKUP_DELAY_MS = 500;

const LOOKUP_FAILED_MESSAGE = "Unable to look up this ABN right now.";

interface RegistrationLookupState {
  registration: BusinessRegistration | null;
  lookupError: string | null;
  isLookingUp: boolean;
}

export const RegistrationLookupContext = createContext<RegistrationLookupState>(
  { registration: null, lookupError: null, isLookingUp: false },
);

/**
 * Looks up the registration for the ABN (or ACN, when there's no ABN) entered on the form,
 * and links it to the business via registrationId.
 *
 * isLookingUp covers the debounce as well as the request, so the form can hold off
 * submitting until the registration is linked.
 */
export const useRegistrationLookup = (ns: string): RegistrationLookupState => {
  const dispatch = useDispatch();
  const { setFieldValue, setFieldTouched, initialValues } = useFormikContext();

  const abnName = withNamespace(ns, "abn");
  const acnName = withNamespace(ns, "acn");
  const registrationIdName = withNamespace(ns, "registrationId");

  const [{ value: abnValue }] = useField(abnName);
  const [{ value: acnValue }] = useField(acnName);
  const [{ value: registrationId }] = useField(registrationIdName);
  const registration = useSelector(getBusinessRegistrationById(registrationId));

  const [lookupError, setLookupError] = useState<string | null>(null);
  const [isLookingUp, setIsLookingUp] = useState(false);
  // Only the most recent lookup may apply its result - an earlier, slower response
  // for a since-edited ABN must not overwrite it.
  const latestLookupRef = useRef<object | null>(null);

  const applyRegistration = useCallback(
    (nextRegistration: BusinessRegistration | null) => {
      const values = {
        registrationId: nextRegistration?.id ?? null,
        tradingName: nextRegistration?.tradingName ?? "",
        isGSTRegistered: nextRegistration?.isGSTRegistered ?? null,
        isABNActive: nextRegistration?.isABNActive ?? null,
        abrLastChecked: nextRegistration?.abrLastChecked ?? null,
        ...(nextRegistration?.abn ? { acn: nextRegistration.acn } : {}),
      };
      // Touched, so a refresh of the business (which still has the old
      // registration until saved) doesn't reset them.
      Object.entries(values).forEach(([field, value]) => {
        setFieldValue(withNamespace(ns, field), value, false);
        setFieldTouched(withNamespace(ns, field), true, false);
      });
    },
    [ns, setFieldValue, setFieldTouched],
  );

  useEffect(() => {
    latestLookupRef.current = null;
    const abn = abnValue ? `${abnValue}` : "";
    const acn = acnValue ? `${acnValue}` : "";
    const initialAbn = `${get(initialValues, abnName) ?? ""}`;
    const initialAcn = `${get(initialValues, acnName) ?? ""}`;
    const isInitialRegistration =
      registrationId === get(initialValues, registrationIdName);

    if (isInitialRegistration && abn === initialAbn && acn === initialAcn) {
      // Untouched - including while the business's registration is still syncing.
      setIsLookingUp(false);
      setLookupError(null);
      return undefined;
    }

    let lookup: { abn: string } | { acn: string } | null = null;
    if (abn) {
      const isCurrentAbn =
        abn === registration?.abn ||
        (isInitialRegistration && abn === initialAbn);
      if (isCurrentAbn || !validateABN(abn)) {
        setIsLookingUp(false);
        setLookupError(null);
        return undefined;
      }
      lookup = { abn };
    } else if (registration?.abn) {
      // Clearing the ABN clears the registration, including the ACN the ABR provided.
      setIsLookingUp(false);
      setLookupError(null);
      applyRegistration(null);
      setFieldValue(acnName, "", false);
      return undefined;
    } else if (acn) {
      const isCurrentAcn =
        registration?.acn === acn ||
        (isInitialRegistration && acn === initialAcn);
      if (isCurrentAcn || !validateACN(acn)) {
        setIsLookingUp(false);
        setLookupError(null);
        return undefined;
      }
      lookup = { acn };
    } else {
      setIsLookingUp(false);
      setLookupError(null);
      if (registrationId) {
        applyRegistration(null);
      }
      return undefined;
    }

    setIsLookingUp(true);
    const lookupToken = {};
    latestLookupRef.current = lookupToken;
    const timeout = setTimeout(() => {
      dispatch(
        lookupBusinessRegistration(lookup, {
          onSuccess: foundRegistration => {
            if (latestLookupRef.current !== lookupToken) {
              return;
            }
            setIsLookingUp(false);
            setLookupError(null);
            applyRegistration(foundRegistration);
          },
          onError: errors => {
            if (latestLookupRef.current !== lookupToken) {
              return;
            }
            setIsLookingUp(false);
            setLookupError(
              errors?.abn?.[0] || errors?.acn?.[0] || LOOKUP_FAILED_MESSAGE,
            );
          },
        }),
      );
    }, LOOKUP_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [
    abnValue,
    acnValue,
    abnName,
    acnName,
    registrationIdName,
    initialValues,
    setFieldValue,
    registration,
    registrationId,
    applyRegistration,
    dispatch,
  ]);

  return { registration, lookupError, isLookingUp };
};
